import { useCallback, useContext, useEffect, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import { PyodideContext } from './PyodideContext';
import { runCell, getNamespace } from '../../hooks/usePyodide';

const HINT_AFTER = 2;
const SOLUTION_AFTER = 5;

export default function ChallengeCell({ cell, onRun }) {
  const { pyodide } = useContext(PyodideContext);
  const [code, setCode] = useState(cell.starter || '');
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [passed, setPassed] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  // Reset when the cell definition changes (e.g. lesson navigation later)
  useEffect(() => {
    setCode(cell.starter || '');
    setOutput(null);
    setAttempts(0);
    setPassed(false);
    setShowSolution(false);
  }, [cell.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheck = useCallback(async () => {
    if (!pyodide || running || passed) return;
    setRunning(true);
    setOutput(null);

    // Append hidden test function after student code
    const fullCode = `${code}\n\n# --- auto-grader ---\n${cell.testFn}`;

    try {
      const result = await runCell(pyodide, fullCode);
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      const success = !result.error && result.stdout.includes('✓');
      if (success) setPassed(true);

      setOutput({ ...result, passed: success });

      const namespace = await getNamespace(pyodide);
      onRun?.({ heading: cell.heading || 'Challenge', code, error: result.error, stdout: result.stdout, stderr: result.stderr, namespace });
    } catch (err) {
      setAttempts((a) => a + 1);
      setOutput({ stdout: '', stderr: '', error: err.message, passed: false });
    } finally {
      setRunning(false);
    }
  }, [pyodide, code, running, passed, attempts, cell, onRun]);

  const showHint = cell.hint && attempts >= HINT_AFTER && !passed;
  const canReveal = cell.solution && attempts >= SOLUTION_AFTER && !passed;

  return (
    <div className={`pylab-challenge-cell${passed ? ' pylab-challenge-cell--passed' : ''}`}>
      <div className="pylab-challenge-header">
        <span className="pylab-challenge-badge">Challenge</span>
        {cell.heading && <span className="pylab-challenge-title">{cell.heading}</span>}
      </div>

      {cell.description && (
        <p className="pylab-challenge-desc">{cell.description}</p>
      )}

      <div className="pylab-editor-wrap">
        <CodeMirror
          value={code}
          extensions={[python()]}
          theme={oneDark}
          onChange={setCode}
          editable={!passed}
          basicSetup={{ lineNumbers: true, foldGutter: false, autocompletion: true, highlightActiveLine: true }}
        />
      </div>

      <div className="pylab-cell-actions">
        {passed ? (
          <span className="pylab-passed-badge">All tests passed!</span>
        ) : (
          <button
            type="button"
            className="pylab-check-btn"
            onClick={handleCheck}
            disabled={!pyodide || running}
          >
            {running
              ? <><span className="pylab-spinner" aria-hidden="true" /> Checking…</>
              : '✓ Check Answer'}
          </button>
        )}
        {attempts > 0 && !passed && (
          <span className="pylab-attempt-count">
            {attempts} attempt{attempts !== 1 ? 's' : ''}
          </span>
        )}
        {canReveal && !showSolution && (
          <button
            type="button"
            className="pylab-reveal-btn"
            onClick={() => setShowSolution(true)}
          >
            Show Solution
          </button>
        )}
      </div>

      {showHint && (
        <div className="pylab-hint">
          <span className="pylab-hint-icon" aria-hidden="true">💡</span>
          <span>{cell.hint}</span>
        </div>
      )}

      {output && (
        <div className={`pylab-output${output.passed ? ' pylab-output--passed' : ''}`}>
          {output.stdout && <pre className="pylab-stdout">{output.stdout}</pre>}
          {output.stderr && <pre className="pylab-stderr">{output.stderr}</pre>}
          {output.error && <pre className="pylab-error">{output.error}</pre>}
        </div>
      )}

      {showSolution && (
        <div className="pylab-solution">
          <p className="pylab-solution-label">Solution</p>
          <pre className="pylab-solution-code">{cell.solution}</pre>
        </div>
      )}
    </div>
  );
}
